
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  DollarSign, 
  Calendar,
  Download,
  CreditCard,
  Clock,
  TrendingUp,
  Plus
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

export const AnalyticsPage = () => {
  const earnings = [
    { month: "Jan", amount: 3200 },
    { month: "Feb", amount: 3800 },
    { month: "Mar", amount: 4200 },
    { month: "Apr", amount: 3900 },
    { month: "May", amount: 4800 },
    { month: "Jun", amount: 5200 }
  ];

  const recentPayments = [
    {
      id: "PAY-123",
      event: "Summer Beats Festival",
      amount: 850,
      status: "Paid",
      date: "2024-02-15"
    },
    {
      id: "PAY-122",
      event: "Club Night Set",
      amount: 400,
      status: "Pending",
      date: "2024-02-10"
    },
    {
      id: "PAY-121",
      event: "Private Event",
      amount: 600,
      status: "Paid",
      date: "2024-02-05"
    }
  ];

  const stats = [
    {
      title: "Total Earnings",
      value: "$25,100",
      change: "+15%",
      icon: DollarSign
    },
    {
      title: "Pending Payments",
      value: "$1,200",
      change: "3 payments",
      icon: Clock
    },
    {
      title: "Average Per Event",
      value: "$450",
      change: "+8%",
      icon: TrendingUp
    }
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Analytics</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, i) => (
          <Card key={i} className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-muted-foreground">{stat.title}</p>
                <h3 className="text-2xl font-bold mt-1">{stat.value}</h3>
              </div>
              <stat.icon className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-green-500 mt-2">{stat.change}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Earnings Overview</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={earnings}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="amount" 
                  stroke="#8884d8" 
                  strokeWidth={2} 
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Payment Methods</h2>
            <Button variant="outline" size="sm">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-4">
            <Button variant="outline" className="w-full justify-start">
              <CreditCard className="mr-2 h-4 w-4" />
              Bank Transfer
            </Button>
            <Button variant="outline" className="w-full justify-start">
              <DollarSign className="mr-2 h-4 w-4" />
              PayPal
            </Button>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Recent Payments</h2>
          <Button variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
        <div className="space-y-4">
          {recentPayments.map((payment) => (
            <div key={payment.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <h3 className="font-medium">{payment.event}</h3>
                <p className="text-sm text-muted-foreground">
                  {payment.id} • {payment.date}
                </p>
              </div>
              <div className="text-right">
                <p className="font-medium">${payment.amount}</p>
                <Badge 
                  variant={payment.status === "Paid" ? "default" : "secondary"}
                >
                  {payment.status}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
